import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { _getAllNotifications, _searchNotifications } from '@/api/notifications/queries';
import {
  clientNotificationType,
  GetNotificationParams,
  INotification,
  INotificationClient,
  notificationFilter,
  notificationPriority,
  notificationRoleScope,
} from '@/api/notifications/types';
import { useGetUserProfile, UserProfileRes } from '@/api/user-dashboard/queries';

const questionnaireNotification: INotificationClient = {
  id: clientNotificationType.QUESTIONNAIRE,
  title: 'Investor’s Questionnaire',
  message: 'We need additional Information from you',
  read: false,
  priority: notificationPriority.HIGH,
  type: clientNotificationType.QUESTIONNAIRE,
  roleScope: notificationRoleScope.USER,
  entityId: null,
  metadata: {},
  createdAt: '',
};

const getKycNotificationByStatus = (
  status?: UserProfileRes['kycStatus'],
): [INotificationClient] | [] =>
  status && status !== 'APPROVED'
    ? [
        {
          id: clientNotificationType.KYC,
          title:
            status === 'IN_PROGRESS'
              ? 'KYC In Progress'
              : status === 'REJECTED'
                ? 'KYC Rejected'
                : 'KYC Required',
          message:
            status === 'IN_PROGRESS'
              ? 'Your KYC verification is currently in progress. Please wait while we review your information.'
              : status === 'REJECTED'
                ? 'Your KYC verification was unsuccessful. Please review your information and try again.'
                : 'Please complete the KYC verification process.',
          read: false,
          priority: notificationPriority.HIGH,
          type: clientNotificationType.KYC,
          roleScope: notificationRoleScope.USER,
          entityId: null,
          metadata: {},
          createdAt: '',
        },
      ]
    : [];

export function getSyntheticNotificationCount(user?: UserProfileRes): number {
  return (
    (user?.hasSubmittedQuestionnaire ? 0 : 1) +
    (user?.kycStatus && user.kycStatus !== 'APPROVED' ? 1 : 0)
  );
}

export function matchesNotificationSearch(notification: INotification, search: string): boolean {
  const normalizedSearch = search.trim().toLocaleLowerCase();
  return (
    notification.title.toLocaleLowerCase().includes(normalizedSearch) ||
    notification.message.toLocaleLowerCase().includes(normalizedSearch)
  );
}

export function getSearchedNotifications(
  serverNotifications: INotification[],
  user: UserProfileRes | undefined,
  params: GetNotificationParams,
): INotification[] {
  const clientNotifications: INotificationClient[] = [
    ...(!user?.hasSubmittedQuestionnaire ? [questionnaireNotification] : []),
    ...getKycNotificationByStatus(user?.kycStatus),
  ];
  const search = params.search?.trim().toLocaleLowerCase() ?? '';
  const client =
    params.filter === notificationFilter.READ
      ? []
      : clientNotifications.filter((notification) =>
          matchesNotificationSearch(notification, search),
        );
  return [...serverNotifications, ...client].sort((left, right) => {
    if (!left.createdAt && !right.createdAt) return 0;
    if (!left.createdAt) return 1;
    if (!right.createdAt) return -1;
    return new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
  });
}

export const useGetAllNotifications = (params?: GetNotificationParams): INotification[] => {
  const { data: serverNotifications } = useQuery({
    ..._getAllNotifications(params),
    initialData: [],
  });

  const { user } = useGetUserProfile();

  const clientNotifications: INotificationClient[] = useMemo(
    () => [
      ...(!user?.hasSubmittedQuestionnaire ? [questionnaireNotification] : []),
      ...getKycNotificationByStatus(user?.kycStatus),
    ],
    [user],
  );

  useEffect(() => {}, []);

  return [
    ...(params?.filter !== notificationFilter.READ ? clientNotifications : []),
    ...(Array.isArray(serverNotifications) ? serverNotifications : []),
  ];
};

export const useSearchNotifications = (params: GetNotificationParams) => {
  const query = useInfiniteQuery(_searchNotifications(params));
  const { user } = useGetUserProfile();
  return {
    ...query,
    notifications: getSearchedNotifications(
      query.data?.pages.flatMap((page) => page) ?? [],
      user,
      params,
    ),
  };
};
